import React, { useState, useEffect } from 'react';
import Highlighter from 'react-highlight-words';
import BankEditForm from './BankEditForm';
import { Input, Button, Space, Table, Popconfirm, Tooltip, Modal } from 'antd';
import { DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { Actions } from '../../store/actions';
import ReactImage from '../../components/Image';

const BankManagement = ({ banks, fetchBanks, deleteBank, updateBank }) => {
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const [editModal, setEditModal] = useState(false);
	const [editBank, setEditBank] = useState();

	useEffect(() => {
		fetchBanks();
	}, []);

	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: function filterDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
			return (
				<div style={{ padding: 8 }}>
					<Input
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
						style={{ width: 188, marginBottom: 8, display: 'block' }}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90 }}
						>
							Search
						</Button>
						<Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
							Reset
						</Button>
					</Space>
				</div>
			);
		},
		filterIcon: function filter(filtered) {
			return <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />;
		},
		onFilter: (value, record) =>
			record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
		render: function render(text) {
			return searchedColumn === dataIndex ? (
				<Highlighter
					highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
					searchWords={[searchText]}
					autoEscape
					textToHighlight={text ? text.toString() : ''}
				/>
			) : (
				text
			);
		},
	});

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');
	};

	const onDelete = async (id) => {
		await deleteBank(id);
		fetchBanks();
	};

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			...getColumnSearchProps('name'),
		},
		{
			title: 'Image',
			dataIndex: 'image',
			key: 'image',
			render: function image(text, record) {
				return (
					<Space size="middle">
						<ReactImage src={record.image} width={50} />
					</Space>
				);
			},
		},
		{
			title: 'URL',
			dataIndex: 'url',
			key: 'url',
			render: function url(text, record) {
				return (
					<Space size="middle">
						<Button href={record.url} target="_blank">
							Visit Official Site
						</Button>
					</Space>
				);
			},
		},
		{
			title: 'Interest Rate',
			dataIndex: 'interestNewCar',
			key: 'interestNewCar',
			render: function url(text, record) {
				return (
					<Space size="middle">{(record?.bankInterest + record?.kiborInterest + record?.insuranceInterest) / 3}</Space>
				);
			},
		},
		{
			title: 'Min Term',
			dataIndex: 'minTerm',
			key: 'minTerm',
			...getColumnSearchProps('minTerm'),
		},
		{
			title: 'Max Term',
			dataIndex: 'maxTerm',
			key: 'maxTerm',
			...getColumnSearchProps('maxTerm'),
		},
		{
			title: 'Min Down Payment for Used',
			dataIndex: 'usedCarDownPayment',
			key: 'usedCarDownPayment',
			render: function downPayment(text, record) {
				return <Space size="middle">{record.usedCarDownPayment ? record.usedCarDownPayment : ''}</Space>;
			},
		},
		{
			title: 'Min Down Payment for New',
			dataIndex: 'newCarDownPayment',
			key: 'newCarDownPayment',
			render: function downPayment(text, record) {
				return <Space size="middle">{record.newCarDownPayment ? record.newCarDownPayment : ''}</Space>;
			},
		},
		{
			title: 'Action',
			key: 'action',
			render: function action(text, record) {
				return (
					<>
						<Tooltip title="Edit Bank">
							<EditOutlined
								onClick={() => {
									setEditBank(record);
									setEditModal(!editModal);
								}}
							/>
						</Tooltip>
						<Popconfirm
							title="Are you sure to delete this bank?"
							onConfirm={() => onDelete(record._id)}
							okText="Yes"
							cancelText="No"
						>
							<DeleteOutlined />
						</Popconfirm>
					</>
				);
			},
		},
	];

	return (
		<div>
			<Table dataSource={banks} columns={columns} rowKey={(record) => record._id} size="small" scroll={{ x: true }} />
			<Modal
				title={`Edit ${editBank?.name}`}
				centered={true}
				visible={editModal}
				footer={null}
				onCancel={() => setEditModal(false)}
				destroyOnClose={true}
			>
				<BankEditForm bank={editBank} updateBank={updateBank} closeModal={() => setEditModal(false)} />
			</Modal>
		</div>
	);
};

const mapStateToProps = (store) => ({
	banks: store.bank.banks,
});

const mapDispatchToProps = (dispatch) => ({
	fetchBanks: () => dispatch(Actions.fetchBanks()),
	deleteBank: (id) => dispatch(Actions.deleteBank(id)),
	updateBank: (id, body) => dispatch(Actions.updateBank(id, body)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BankManagement);
